class ProductDetailsTabs {
  constructor() {
    this.tabButtons = document.querySelectorAll('.product-details-tabs .tab-button');
    this.tabContents = document.querySelectorAll('.product-details-tabs .tab-content');
    this.readMoreButton = document.querySelector('.product-details-tabs .read-more-button');
    this.descriptionContent = document.querySelector('.product-details-tabs .description-content');
    this.tabsList = document.querySelector('.product-details-tabs .tabs-list');
    
    // Accordion elements
    this.accordionHeaders = document.querySelectorAll('.product-details-tabs .accordion-header');
    this.accordionContents = document.querySelectorAll('.product-details-tabs .accordion-content');
    this.accordionReadMoreButtons = document.querySelectorAll('.product-details-tabs .accordion-content .read-more-button');
    
    this.init();
  }
  
  init() {
    this.initTabs();
    this.initAccordion();
    this.initReadMore();
    this.extractImageUrlsFromWarningLabel();
    this.setupScrollingTabs();
  }
  
  initTabs() {
    if (!this.tabButtons.length) return;
    
    this.tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        // Don't re-process if tab is already active
        if (button.classList.contains('active')) return;
        
        // Deactivate all tabs
        this.tabButtons.forEach(btn => {
          btn.classList.remove('active');
          btn.setAttribute('aria-selected', 'false');
        });
        
        // Hide all tab contents with a smooth transition
        this.tabContents.forEach(content => {
          if (content.classList.contains('active')) {
            // Smoothly fade out active content
            content.style.opacity = '0';
            setTimeout(() => {
              content.classList.remove('active');
              content.style.opacity = '';
            }, 200);
          } else {
            content.classList.remove('active');
          }
        });
        
        // Activate clicked tab after a short delay to allow for smooth transitions
        setTimeout(() => {
          button.classList.add('active');
          button.setAttribute('aria-selected', 'true');
          
          const tabId = button.getAttribute('data-tab');
          const activeContent = document.getElementById(tabId);
          if (activeContent) {
            activeContent.classList.add('active');
          }
          
          // Scroll the active tab into view for mobile
          this.scrollActiveTabIntoView(button);
        }, 100);
      });
    });
  }
  
  initAccordion() {
    if (!this.accordionHeaders.length) return;
    
    this.accordionHeaders.forEach(header => {
      header.addEventListener('click', () => {
        const accordionId = header.getAttribute('data-accordion');
        const accordionContent = document.getElementById(accordionId);
        const isActive = header.classList.contains('active');
        
        if (isActive) {
          // Close accordion
          this.closeAccordion(header, accordionContent);
        } else {
          // Open accordion (don't close others - allow multiple open)
          this.openAccordion(header, accordionContent);
        }
      });
    });
  }
  
  openAccordion(header, content) {
    if (!header || !content) return;
    
    // Activate current accordion
    header.classList.add('active');
    content.classList.add('active');
    
    // Set the actual content height for smooth animation
    const actualHeight = content.scrollHeight;
    content.style.maxHeight = actualHeight + 'px';
    
    // Reset max-height after animation to allow dynamic content
    setTimeout(() => {
      if (content.classList.contains('active')) {
        content.style.maxHeight = 'none';
      }
    }, 400);
  }
  
  closeAccordion(header, content) {
    if (!header || !content) return;
    
    // Set current height first for smooth closing animation
    const currentHeight = content.scrollHeight;
    content.style.maxHeight = currentHeight + 'px';
    
    // Force a reflow
    content.offsetHeight;
    
    // Start closing animation
    content.style.maxHeight = '0px';
    header.classList.remove('active');
    
    // Remove active class after animation
    setTimeout(() => {
      content.classList.remove('active');
      content.style.maxHeight = '';
    }, 400);
  }
  
  setupScrollingTabs() {
    if (!this.tabsList) return;
    
    // Check if we're on mobile
    const isMobile = window.innerWidth <= 990;
    
    if (isMobile) {
      // Add scroll indicator if needed
      const tabsContainer = this.tabsList.parentElement;
      if (tabsContainer && !tabsContainer.querySelector('.tabs-scroll-indicator')) {
        const scrollIndicator = document.createElement('div');
        scrollIndicator.className = 'tabs-scroll-indicator';
        scrollIndicator.innerHTML = '→';
        tabsContainer.appendChild(scrollIndicator);
        
        // Show/hide indicator based on scroll position
        this.updateScrollIndicator();
        
        // Update indicator on scroll
        this.tabsList.addEventListener('scroll', this.debounce(() => {
          this.updateScrollIndicator();
        }, 150));
      }
      
      // Scroll active tab into view on page load
      const activeTab = this.tabsList.querySelector('.tab-button.active');
      if (activeTab) {
        this.scrollActiveTabIntoView(activeTab);
      }
    }
    
    // Handle window resize
    window.addEventListener('resize', this.debounce(() => {
      const newIsMobile = window.innerWidth <= 990;
      if (newIsMobile) {
        this.setupScrollingTabs();
      } else {
        // Remove scroll indicator on desktop
        const indicator = document.querySelector('.tabs-scroll-indicator');
        if (indicator) indicator.remove();
      }
    }, 250));
  }
  
  updateScrollIndicator() {
    if (!this.tabsList) return;
    
    const scrollIndicator = document.querySelector('.tabs-scroll-indicator');
    if (!scrollIndicator) return;
    
    // Check if we can scroll more to the right
    const canScrollMore = this.tabsList.scrollWidth > this.tabsList.clientWidth && 
                           this.tabsList.scrollLeft < (this.tabsList.scrollWidth - this.tabsList.clientWidth - 10);
    
    // Show/hide indicator based on scroll position
    scrollIndicator.style.opacity = canScrollMore ? '0.7' : '0';
  }
  
  scrollActiveTabIntoView(activeTab) {
    if (!this.tabsList || !activeTab) return;
    
    // Only scroll on mobile
    if (window.innerWidth > 990) return;
    
    // Calculate position to scroll
    const tabPosition = activeTab.offsetLeft;
    const tabWidth = activeTab.offsetWidth;
    const containerWidth = this.tabsList.clientWidth;
    
    // Calculate target scroll position (center the tab)
    let scrollTo = tabPosition - (containerWidth / 2) + (tabWidth / 2);
    
    // Ensure we don't scroll beyond bounds
    scrollTo = Math.max(0, Math.min(scrollTo, this.tabsList.scrollWidth - containerWidth));
    
    // Smooth scroll
    this.tabsList.scrollTo({
      left: scrollTo,
      behavior: 'smooth'
    });
    
    // Update indicator after scrolling
    setTimeout(() => this.updateScrollIndicator(), 300);
  }
  
  initReadMore() {
    // Handle read more buttons in both tabs and accordion
    const allReadMoreButtons = document.querySelectorAll('.product-details-tabs .read-more-button');
    
    allReadMoreButtons.forEach(button => {
      const container = button.closest('.tab-content') || button.closest('.accordion-content');
      const descriptionContent = container?.querySelector('.description-content');
      
      if (!descriptionContent) return;
      
      // Check if read more is needed
      this.checkReadMoreVisibility(button, descriptionContent);
      
      button.addEventListener('click', () => {
        this.toggleReadMore(button, descriptionContent);
      });
    });
    
    // Check on resize
    window.addEventListener('resize', this.debounce(() => {
      allReadMoreButtons.forEach(button => {
        const container = button.closest('.tab-content') || button.closest('.accordion-content');
        const descriptionContent = container?.querySelector('.description-content');
        if (descriptionContent) {
          this.checkReadMoreVisibility(button, descriptionContent);
        }
      });
    }, 250));
  }
  
  checkReadMoreVisibility(button, descriptionContent) {
    if (!descriptionContent) return;
    
    if (descriptionContent.scrollHeight <= descriptionContent.clientHeight) {
      if (button) {
        button.classList.add('hidden');
      }
      descriptionContent.classList.add('expanded');
    } else {
      if (button) {
        button.classList.remove('hidden');
      }
    }
  }
  
  toggleReadMore(button, descriptionContent) {
    if (!descriptionContent || !button) return;
    
    descriptionContent.classList.toggle('expanded');
    button.classList.toggle('expanded');
    
    if (descriptionContent.classList.contains('expanded')) {
      button.textContent = 'Read Less ';
      const minusIcon = document.createElement('span');
      minusIcon.className = 'read-more-icon-minus';
      minusIcon.textContent = '-';
      button.appendChild(minusIcon);
    } else {
      button.textContent = 'Read More ';
      const plusIcon = document.createElement('span');
      plusIcon.className = 'read-more-icon-plus';
      plusIcon.textContent = '+';
      button.appendChild(plusIcon);
    }
  }
  
  extractImageUrlsFromWarningLabel() {
    // Handle warning content in both tabs and accordion
    const warningContents = document.querySelectorAll('.product-details-tabs .warning-content');
    const warningImages = document.querySelectorAll('.product-details-tabs .warning-images');
    
    if (!warningContents.length || !warningImages.length) return;
    
    // Wait a bit to ensure content is fully rendered
    setTimeout(() => {
      warningContents.forEach((warningContent, index) => {
        const warningImage = warningImages[index];
        if (!warningContent || !warningImage) return;
        
        const contentHtml = warningContent.innerHTML;
        
        // Find image URL patterns (case insensitive and work with metafield_tag)
        const imageUrlRegex = /image_url:?\s*(https?:\/\/[^\s<"']+\.(jpg|jpeg|png|gif|webp)(\?[^\s<"']*)?)/gi;
        let matches;
        const foundUrls = [];
        let newContentHtml = contentHtml;
        
        while ((matches = imageUrlRegex.exec(contentHtml)) !== null) {
          const fullMatch = matches[0];
          const imageUrl = matches[1];
          foundUrls.push(imageUrl);
          
          // Remove the URL text from the content
          newContentHtml = newContentHtml.replace(fullMatch, '');
        }
        
        // Only update content if we found URLs
        if (foundUrls.length > 0) {
          // Update content without the URLs
          warningContent.innerHTML = newContentHtml;
          
          // Add images to the warning images section
          foundUrls.forEach(url => {
            const img = document.createElement('img');
            img.src = url;
            img.alt = 'Warning Label Image';
            img.className = 'tw-category-image';
            warningImage.appendChild(img);
          });
        }
      });
      
      // Check for TW Category tags
      this.checkForTWCategoryTags();
    }, 300);
  }
  
  checkForTWCategoryTags() {
    // Get product tags if they're available in the page
    const productTagsElement = document.querySelector('meta[property="product:tag"]');
    if (!productTagsElement) return;
    
    const tags = productTagsElement.getAttribute('content').split(',');
    const allWarningImages = document.querySelectorAll('.product-details-tabs .warning-images');
    
    if (!allWarningImages.length) return;
    
    allWarningImages.forEach(warningImages => {
      // Check for specific TW category tags
      if (tags.includes('TW Cat 1')) {
        this.addCategoryImage(warningImages, 'https://cdn.shopify.com/s/files/1/0523/6729/4661/files/TW1.jpg?v=1746074596', 'TW Cat 1 Image');
      }
      
      if (tags.includes('TW Cat 2')) {
        this.addCategoryImage(warningImages, 'https://cdn.shopify.com/s/files/1/0523/6729/4661/files/TW2.jpg?v=1746074596', 'TW Cat 2 Image');
      }
      
      if (tags.includes('TW Cat 3')) {
        this.addCategoryImage(warningImages, 'https://cdn.shopify.com/s/files/1/0523/6729/4661/files/TW3.jpg?v=1746074596', 'TW Cat 3 Image');
      }
    });
  }
  
  addCategoryImage(container, url, alt) {
    const img = document.createElement('img');
    img.src = url;
    img.alt = alt;
    img.className = 'tw-category-image';
    container.appendChild(img);
  }
  
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize on DOMContentLoaded and when Shopify section renders
document.addEventListener('DOMContentLoaded', () => {
  new ProductDetailsTabs();
});

// Support for Shopify section rendering
document.addEventListener('shopify:section:load', (event) => {
  if (event.target.querySelector('.product-details-tabs')) {
    new ProductDetailsTabs();
  }
}); 